import { useEffect, useState } from 'react';
import {
  EbmItemClassification,
  fetchItemClassifications,
} from '@/renderer/services/ebmItemClassificationService';

const PAGE_SIZE = 20;

/**
 * Server-paginated, searchable option source for the item-classification
 * DropdownSelect on the fiscal setup pages — mirrors the searchable +
 * hasMoreOptions/onLoadMoreOptions pattern already established for
 * server-paginated dropdowns elsewhere (see CLAUDE.md's Dropdown selects
 * UI standard).
 */
export function useItemClassificationOptions() {
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(1);
  const [items, setItems] = useState<EbmItemClassification[]>([]);
  const [hasMore, setHasMore] = useState(false);
  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    let cancelled = false;
    setIsLoading(true);
    (async () => {
      const res = await fetchItemClassifications({
        search,
        page: 1,
        pageSize: PAGE_SIZE,
      });
      if (cancelled) return;
      if (res.success && res.data) {
        setItems(res.data.data);
        setHasMore(res.data.pagination.hasNextPage);
        setPage(1);
      }
      setIsLoading(false);
    })().catch(() => {
      if (!cancelled) setIsLoading(false);
    });
    return () => {
      cancelled = true;
    };
  }, [search]);

  const loadMore = async () => {
    if (isLoading || !hasMore) return;
    setIsLoading(true);
    const nextPage = page + 1;
    const res = await fetchItemClassifications({
      search,
      page: nextPage,
      pageSize: PAGE_SIZE,
    });
    if (res.success && res.data) {
      setItems((prev) => [...prev, ...res.data!.data]);
      setHasMore(res.data.pagination.hasNextPage);
      setPage(nextPage);
    }
    setIsLoading(false);
  };

  return {
    items,
    search,
    setSearch,
    hasMore,
    isLoading,
    loadMore,
  };
}
